/**
 * MobileHeader.tsx
 * Phase 1p Mobile Nav + A11y SSOT — Wave 3 Stage 2
 * Issue #3028
 *
 * Mobile-only simplified header
 * - タイトルのみ表示 (breadcrumb なし)
 * - Left: back button (showBack=true 時)
 * - Right: hamburger (drawer trigger)
 *
 * A11y:
 * - role="banner" + aria-label="ヘッダー"
 * - .ripia-focus-ring utility 適用 (Phase 1p SSOT)
 *
 * Scope OUT: breadcrumb (desktop DashboardLayout 側で維持)
 */

import * as React from 'react';
import { ArrowLeft, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * MobileHeaderProps — discriminated union で showBack/onBack invariant を型で保証
 * - showBack=true の場合 onBack 必須 (click で noop 防止)
 * - showBack=false/未指定の場合 onBack は禁止 (dead prop 防止)
 */
type MobileHeaderBackProps =
  | { showBack: true; onBack: () => void }
  | { showBack?: false; onBack?: never };

export type MobileHeaderProps = MobileHeaderBackProps & {
  /** ページタイトル */
  title: string;
  /** Drawer を開くコールバック */
  onOpenDrawer: () => void;
  /** Drawer の open state (aria-expanded で SR に状態伝達) */
  drawerOpen: boolean;
};

// ---------------------------------------------------------------------------
// MobileHeader component
// ---------------------------------------------------------------------------

/**
 * MobileHeader
 *
 * mobile viewport (md 未満) にのみ表示するシンプルなヘッダー。
 * sticky top-0 に配置し、breadcrumb は表示しない。
 * desktop は DashboardLayout の既存 breadcrumb header を維持。
 *
 * @example
 * ```tsx
 * <MobileHeader
 *   title="予約管理"
 *   showBack={false}
 *   onOpenDrawer={() => setDrawerOpen(true)}
 * />
 * ```
 */
export function MobileHeader(props: MobileHeaderProps): React.ReactElement {
  const { title, onOpenDrawer, drawerOpen } = props;
  // discriminated union により showBack=true 時は onBack が存在することが型で保証される
  const showBack = props.showBack === true;
  const onBack = showBack ? props.onBack : undefined;
  return (
    <header
      role="banner"
      aria-label="ヘッダー"
      data-testid="mobile-header"
      className={cn(
        'sticky top-0 z-30',
        'flex h-14 items-center gap-2 px-3',
        'bg-[var(--paper)] border-b border-[var(--line)]',
        // Safe area padding for iOS notch
        'pt-[env(safe-area-inset-top,0px)]'
      )}
    >
      {/* Left: back button or spacer */}
      <div className="flex w-9 items-center justify-start">
        {showBack && (
          <button
            type="button"
            onClick={onBack}
            className={cn(
              'ripia-focus-ring',
              'flex h-9 w-9 items-center justify-center rounded-md',
              'text-[var(--ink-2)] hover:text-[var(--ink)]',
              'hover:bg-[var(--brand-hover)]',
              'transition-colors duration-[var(--dur-fast)]'
            )}
            aria-label="前のページに戻る"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Center: page title */}
      <h1
        className={cn(
          'flex-1 text-center',
          'text-base font-semibold leading-none',
          'text-[var(--ink)] truncate'
        )}
      >
        {title}
      </h1>

      {/* Right: hamburger menu */}
      <div className="flex w-9 items-center justify-end">
        <button
          type="button"
          onClick={onOpenDrawer}
          className={cn(
            'ripia-focus-ring',
            'flex h-9 w-9 items-center justify-center rounded-md',
            'text-[var(--ink-2)] hover:text-[var(--ink)]',
            'hover:bg-[var(--brand-hover)]',
            'transition-colors duration-[var(--dur-fast)]'
          )}
          aria-label="メニューを開く"
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}
